import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { animalesAPI, reportesAPI } from '../services/api';
import { GenealogiaSection } from '../components/Animales/GenealogiaSection';
import { formatDate, calcularEdad, getCategoriaColor, getEstadoColor } from '../utils/helpers';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

// Frame Score reference ranges by breed (matching backend)
const FRAME_REF = {
  angus: { macho: [4,6], hembra: [3,5] },
  hereford: { macho: [4,6], hembra: [3,5] },
  'polled hereford': { macho: [4,6], hembra: [3,5] },
  shorthorn: { macho: [4,6], hembra: [4,6] },
  brangus: { macho: [5,7], hembra: [4,6] },
  braford: { macho: [5,7], hembra: [4,6] },
  brahman: { macho: [5,7], hembra: [5,7] },
  nelore: { macho: [5,7], hembra: [4,6] },
  charolais: { macho: [6,8], hembra: [5,7] },
  limousin: { macho: [5,7], hembra: [5,7] },
  simmental: { macho: [6,8], hembra: [5,7] },
  criolla: { macho: [3,5], hembra: [3,5] },
};

const FrameBadge = ({ frame, raza, sexo }) => {
  const razaKey = (raza || '').toLowerCase().trim();
  let ref = FRAME_REF[razaKey];
  if (!ref) {
    for (const [k, v] of Object.entries(FRAME_REF)) {
      if (razaKey.includes(k) || k.includes(razaKey)) { ref = v; break; }
    }
  }
  if (!ref) return null;
  const [min, max] = ref[sexo] || ref.macho || [4,6];
  let label, cls;
  if (frame < min) { label = 'Bajo'; cls = 'bg-amber-100 text-amber-800'; }
  else if (frame > max) { label = 'Alto'; cls = 'bg-blue-100 text-blue-800'; }
  else { label = 'Adecuado'; cls = 'bg-green-100 text-green-800'; }
  return (
    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${cls}`}
      data-testid="frame-badge" title={`Rango ideal ${raza}: ${min}-${max}`}>
      {label}
    </span>
  );
};

const GenealogyCard = ({ label, animalId, navigate }) => {
  const [parent, setParent] = React.useState(null);
  React.useEffect(() => {
    animalesAPI.getById(animalId).then(res => setParent(res.data?.animal)).catch(() => {});
  }, [animalId]);
  if (!parent) return <div className="bg-white rounded-lg p-3 text-sm text-gray-400">Cargando {label}...</div>;
  return (
    <div className="bg-white rounded-lg p-3 border border-indigo-100 cursor-pointer hover:border-indigo-300 transition"
      onClick={() => navigate(`/animales/${animalId}`)} data-testid={`genealogia-${label.toLowerCase()}`}>
      <p className="text-xs text-indigo-600 font-semibold mb-1">{label}</p>
      <p className="font-bold text-gray-900">VID: {parent.VID}</p>
      <p className="text-xs text-gray-500">IDE: {parent.IDE || '-'} | {parent.raza} | {parent.categoria?.replace('_',' ')}</p>
    </div>
  );
};

const AnimalDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ VID: '', IDE: '', categoria: '', propietario: '', estado: '', observaciones: '' });
  const [saving, setSaving] = useState(false);
  const [editMsg, setEditMsg] = useState('');
  const [editError, setEditError] = useState('');

  useEffect(() => {
    cargarDatosAnimal();
  }, [id]);

  const cargarDatosAnimal = async () => {
    try {
      setLoading(true);
      const response = await animalesAPI.getById(id);
      setData(response.data);
      // Pre-fill edit data
      const a = response.data?.animal;
      if (a) {
        setEditData({
          VID: a.VID || '',
          IDE: a.IDE || '',
          categoria: a.categoria || '',
          propietario: a.propietario || '',
          estado: a.estado || 'activo',
          observaciones: a.observaciones || '',
        });
      }
    } catch (err) {
      setError('Error al cargar los datos del animal');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setEditMsg('');
    setEditError('');
    try {
      const payload = {};
      if (editData.VID && editData.VID !== animal.VID) payload.VID = editData.VID;
      if (editData.IDE !== (animal.IDE || '')) payload.IDE = editData.IDE || null;
      if (editData.categoria && editData.categoria !== animal.categoria) payload.categoria = editData.categoria;
      if (editData.propietario !== (animal.propietario || '')) payload.propietario = editData.propietario || null;
      if (editData.estado && editData.estado !== animal.estado) payload.estado = editData.estado;
      if (editData.observaciones !== (animal.observaciones || '')) payload.observaciones = editData.observaciones || null;

      if (Object.keys(payload).length === 0) {
        setEditMsg('No hay cambios para guardar');
        setSaving(false);
        return;
      }

      await animalesAPI.update(id, payload);
      setEditMsg('Datos actualizados correctamente');
      setEditMode(false);
      cargarDatosAnimal(); // Reload data
    } catch (err) {
      const detail = err.response?.data?.detail;
      setEditError(typeof detail === 'string' ? detail : 'Error al actualizar');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">🐄</div>
          <p className="text-gray-600">Cargando datos del animal...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || 'Animal no encontrado'}
        </div>
        <button
          onClick={() => navigate('/animales')}
          className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          ← Volver a Animales
        </button>
      </div>
    );
  }

  const { animal, pesajes = [], servicios = [], pariciones = [], controles_machos = [], alertas = [], eventos_sanitarios = [], metricas_avanzadas = {} } = data;

  // Ordenar eventos por fecha para timeline
  const todosEventos = [
    ...pesajes.map(p => ({ tipo: 'pesaje', fecha: p.fecha, data: p })),
    ...servicios.map(s => ({ tipo: 'servicio', fecha: s.fecha, data: s })),
    ...pariciones.map(p => ({ tipo: 'paricion', fecha: p.fecha_paricion, data: p })),
    ...controles_machos.map(c => ({ tipo: 'control', fecha: c.fecha, data: c })),
    ...eventos_sanitarios.map(e => ({ tipo: e.tipo || 'sanitario', fecha: e.fecha, data: e })),
  ].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

  return (
    <div className="p-6">
      {/* Header con botón volver */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/animales')}
          className="mb-4 px-4 py-2 text-gray-600 hover:text-gray-900 flex items-center space-x-2"
          data-testid="back-to-animales-btn"
        >
          <span>←</span>
          <span>Volver a Animales</span>
        </button>
      </div>

      {/* Datos principales del animal */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-start space-x-4">
            <div className="text-6xl">
              {animal.sexo === 'hembra' ? '🐄' : '🐂'}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Animal {animal.VID}
              </h1>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getCategoriaColor(animal.categoria)}`}>
                  {animal.categoria?.replace('_', ' ') || '-'}
                </span>
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getEstadoColor(animal.estado)}`}>
                  {animal.estado}
                </span>
              </div>
            </div>
          </div>

          {/* Acciones rápidas */}
          <div className="flex flex-col space-y-2">
            <button
              onClick={() => setEditMode(!editMode)}
              className={`px-4 py-2 border rounded-lg text-sm font-medium transition ${editMode ? 'bg-gray-100 text-gray-700 border-gray-300' : 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'}`}
              data-testid="edit-animal-btn"
            >
              {editMode ? 'Cancelar Edicion' : 'Editar Identificacion'}
            </button>
            <button
              onClick={() => navigate(`/sesiones?animal=${id}`)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              + Nuevo Evento
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await reportesAPI.animalPDF(id);
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `animal_${animal.VID || id}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                } catch { alert('Error al generar PDF'); }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              data-testid="download-pdf-btn"
            >
              Download PDF
            </button>
          </div>
        </div>

        {/* Edit mode banner */}
        {editMode && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-amber-800">Editando identificacion del animal</p>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                data-testid="save-edit-btn"
              >
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
            {editMsg && <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2 mb-3">{editMsg}</p>}
            {editError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3">{editError}</p>}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">VID (Visual) *</label>
                <input type="text" value={editData.VID} onChange={(e) => setEditData(p => ({ ...p, VID: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400" data-testid="edit-vid" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">IDE (RFID)</label>
                <input type="text" value={editData.IDE} onChange={(e) => setEditData(p => ({ ...p, IDE: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-amber-400" data-testid="edit-ide" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Categoria</label>
                <select value={editData.categoria} onChange={(e) => setEditData(p => ({ ...p, categoria: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="edit-categoria">
                  <option value="">--</option>
                  <option value="ternero">Ternero</option>
                  <option value="desmamante">Desmamante</option>
                  <option value="vaquilla">Vaquilla</option>
                  <option value="novillo">Novillo</option>
                  <option value="vaca">Vaca</option>
                  <option value="toro">Toro</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Propietario</label>
                <input type="text" value={editData.propietario} onChange={(e) => setEditData(p => ({ ...p, propietario: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="edit-propietario" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Estado</label>
                <select value={editData.estado} onChange={(e) => setEditData(p => ({ ...p, estado: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="edit-estado">
                  <option value="activo">Activo</option>
                  <option value="vendido">Vendido</option>
                  <option value="muerto">Muerto</option>
                  <option value="descartado">Descartado</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Observaciones</label>
                <input type="text" value={editData.observaciones} onChange={(e) => setEditData(p => ({ ...p, observaciones: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="edit-observaciones" />
              </div>
            </div>
          </div>
        )}

        {/* Grid de información básica */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500">VID (Visual)</p>
            <p className="text-lg font-semibold text-gray-900">{animal.VID}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">IDE (RFID)</p>
            <p className="text-lg font-mono font-semibold text-gray-900">
              {animal.IDE || 'Sin IDE'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Sexo</p>
            <p className="text-lg font-semibold text-gray-900 capitalize">{animal.sexo}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Edad</p>
            <p className="text-lg font-semibold text-gray-900">
              {calcularEdad(animal.fecha_nacimiento)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Fecha Nacimiento</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatDate(animal.fecha_nacimiento)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Raza</p>
            <p className="text-lg font-semibold text-gray-900">{animal.raza}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Propietario</p>
            <p className="text-lg font-semibold text-gray-900">{animal.propietario || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Fecha Ingreso</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatDate(animal.fecha_ingreso)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Eventos</p>
            <p className="text-lg font-semibold text-gray-900">{todosEventos.length}</p>
          </div>
        </div>

        {/* Genealogía — Madre / Padre (editable) */}
        <GenealogiaSection animal={animal} navigate={navigate} onSaved={cargarDatosAnimal} />

        {/* Alertas activas */}
        {alertas.length > 0 && (
          <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
            <p className="text-sm font-medium text-yellow-800 mb-2">
              ⚠️ {alertas.length} Alerta{alertas.length > 1 ? 's' : ''} Activa{alertas.length > 1 ? 's' : ''}
            </p>
            {alertas.map((alerta, idx) => (
              <p key={idx} className="text-sm text-yellow-700">
                • {alerta.descripcion}
              </p>
            ))}
          </div>
        )}

        {/* Observaciones */}
        {animal.observaciones && (
          <div className="mt-4 p-4 bg-gray-50 rounded">
            <p className="text-sm font-medium text-gray-700 mb-1">Observaciones:</p>
            <p className="text-sm text-gray-600">{animal.observaciones}</p>
          </div>
        )}
      </div>

      {/* Métricas Avanzadas DON FELIX */}
      {Object.keys(metricas_avanzadas).length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6" data-testid="metricas-avanzadas">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Indicadores Productivos</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {metricas_avanzadas.kg_acumulados_destetados != null && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-black text-green-700">{metricas_avanzadas.kg_acumulados_destetados}</p>
                <p className="text-xs text-green-600 mt-1">Kg Acumulados Destetados</p>
              </div>
            )}
            {metricas_avanzadas.total_terneros_destetados != null && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-black text-blue-700">{metricas_avanzadas.total_terneros_destetados}</p>
                <p className="text-xs text-blue-600 mt-1">Terneros Destetados</p>
              </div>
            )}
            {metricas_avanzadas.ep_promedio_individual != null && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-black text-amber-700">{metricas_avanzadas.ep_promedio_individual}%</p>
                <p className="text-xs text-amber-600 mt-1">EP Promedio Individual</p>
              </div>
            )}
            {metricas_avanzadas.tasa_crecimiento != null && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-black text-purple-700">{metricas_avanzadas.tasa_crecimiento}%</p>
                <p className="text-xs text-purple-600 mt-1">Tasa Crecimiento</p>
              </div>
            )}
            {metricas_avanzadas.gdp_kg_dia != null && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-black text-indigo-700">{metricas_avanzadas.gdp_kg_dia}</p>
                <p className="text-xs text-indigo-600 mt-1">GDP (kg/dia) en {metricas_avanzadas.gdp_periodo_dias}d</p>
              </div>
            )}
            {metricas_avanzadas.relacion_peso_alzada != null && (
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-black text-teal-700">{metricas_avanzadas.relacion_peso_alzada}</p>
                <p className="text-xs text-teal-600 mt-1">Rel. Peso/Alzada (kg/cm)</p>
              </div>
            )}
            {metricas_avanzadas.productividad_ua != null && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                <p className="text-2xl font-black text-emerald-700">{metricas_avanzadas.productividad_ua}</p>
                <p className="text-xs text-emerald-600 mt-1">Productividad/UA (kg)</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs de navegación */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'general', label: '📋 General', count: null },
              { id: 'pesajes', label: '⚖️ Pesajes', count: pesajes.length },
              { id: 'reproductivo', label: '💑 Reproductivo', count: servicios.length + pariciones.length },
              { id: 'controles', label: '🐂 Controles', count: controles_machos.length },
              { id: 'sanitario', label: '💊 Sanitario', count: eventos_sanitarios.length },
              { id: 'timeline', label: '📅 Timeline', count: todosEventos.length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.count !== null && (
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Contenido de tabs */}
        <div className="p-6">
          {/* TAB: GENERAL */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen del Animal</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-600 mb-1">Total Pesajes</p>
                    <p className="text-2xl font-bold text-blue-900">{pesajes.length}</p>
                  </div>
                  {animal.sexo === 'hembra' && (
                    <>
                      <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                        <p className="text-sm text-pink-600 mb-1">Servicios</p>
                        <p className="text-2xl font-bold text-pink-900">{servicios.length}</p>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <p className="text-sm text-purple-600 mb-1">Pariciones</p>
                        <p className="text-2xl font-bold text-purple-900">{pariciones.length}</p>
                      </div>
                    </>
                  )}
                  {animal.sexo === 'macho' && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-sm text-orange-600 mb-1">Controles</p>
                      <p className="text-2xl font-bold text-orange-900">{controles_machos.length}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Genealogía */}
              {(animal.madre_id || animal.padre_id || animal.padre_externo) && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Genealogia</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {animal.madre_id && (
                      <GenealogyCard label="Madre" animalId={animal.madre_id} navigate={navigate} />
                    )}
                    {animal.padre_id && (
                      <GenealogyCard label="Padre" animalId={animal.padre_id} navigate={navigate} />
                    )}
                    {!animal.madre_id && (
                      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-400">Madre no registrada</div>
                    )}
                    {!animal.padre_id && animal.padre_externo && (
                      <div className="bg-white rounded-lg p-3 border border-indigo-100">
                        <p className="text-xs text-indigo-600 font-semibold mb-1">Padre (externo / semen)</p>
                        <p className="font-bold text-gray-900">{animal.padre_externo}</p>
                      </div>
                    )}
                    {!animal.padre_id && !animal.padre_externo && (
                      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-400">Padre no registrado</div>
                    )}
                  </div>
                </div>
              )}
              {!animal.madre_id && !animal.padre_id && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Genealogia</h3>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-400 text-center">
                    Sin genealogia registrada
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB: PESAJES */}
          {activeTab === 'pesajes' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Historial de Pesajes ({pesajes.length})
                </h3>
                <div className="flex gap-2">
                  {pesajes.length >= 2 && (
                    <button
                      onClick={async (e) => {
                        const btn = e.currentTarget;
                        btn.textContent = 'Recalculando...';
                        btn.disabled = true;
                        try {
                          await animalesAPI.recalcularGMD(id);
                          cargarDatosAnimal();
                          btn.textContent = 'Recalcular GMD';
                          btn.disabled = false;
                        } catch {
                          btn.textContent = 'Error - Reintentar';
                          btn.disabled = false;
                        }
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
                      data-testid="recalcular-gmd-btn"
                    >
                      Recalcular GMD
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/sesiones?tipo=pesaje&animal=${id}`)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    + Nuevo Pesaje
                  </button>
                </div>
              </div>

              {/* Weight Evolution Chart */}
              {pesajes.length >= 2 && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6" data-testid="peso-evolution-chart">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Evolucion de Peso</h4>
                  <div className="h-64">
                    <Line
                      data={{
                        labels: [...pesajes].reverse().map(p => {
                          const d = p.fecha ? p.fecha.substring(0, 10) : '';
                          return d;
                        }),
                        datasets: [
                          {
                            label: 'Peso (kg)',
                            data: [...pesajes].reverse().map(p => p.peso),
                            borderColor: '#16a34a',
                            backgroundColor: 'rgba(22,163,74,0.1)',
                            fill: true,
                            tension: 0.3,
                            pointRadius: 5,
                            pointBackgroundColor: '#16a34a',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                          },
                          ...(pesajes.some(p => p.gmd != null) ? [{
                            label: 'GMD (kg/dia x100)',
                            data: [...pesajes].reverse().map(p => p.gmd != null ? Math.round(p.gmd * 100) : null),
                            borderColor: '#2563eb',
                            backgroundColor: 'rgba(37,99,235,0.08)',
                            fill: false,
                            tension: 0.3,
                            borderDash: [5, 5],
                            pointRadius: 3,
                            yAxisID: 'y1',
                          }] : []),
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { intersect: false, mode: 'index' },
                        plugins: {
                          legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
                          tooltip: {
                            callbacks: {
                              afterBody: (items) => {
                                const idx = items[0]?.dataIndex;
                                if (idx == null) return '';
                                const reversed = [...pesajes].reverse();
                                const p = reversed[idx];
                                const lines = [];
                                if (p?.altura_cadera) lines.push(`Altura: ${p.altura_cadera} cm`);
                                if (p?.frame) lines.push(`Frame: ${p.frame}`);
                                if (p?.gmd != null) lines.push(`GMD: ${p.gmd.toFixed(2)} kg/dia`);
                                if (p?.edad_meses) lines.push(`Edad: ${p.edad_meses} meses`);
                                return lines.join('\n');
                              }
                            }
                          }
                        },
                        scales: {
                          y: { title: { display: true, text: 'Peso (kg)', font: { size: 11 } }, beginAtZero: false },
                          ...(pesajes.some(p => p.gmd != null) ? {
                            y1: { position: 'right', title: { display: true, text: 'GMD x100', font: { size: 11 } }, grid: { drawOnChartArea: false } }
                          } : {}),
                          x: { ticks: { font: { size: 10 }, maxRotation: 45 } },
                        },
                      }}
                    />
                  </div>
                  {/* Summary stats */}
                  {(() => {
                    const primerPeso = pesajes[pesajes.length - 1]?.peso || 0;
                    const ultimoPeso = pesajes[0]?.peso || 0;
                    const gananciaTotal = ultimoPeso - primerPeso;
                    const fechaPrimera = pesajes[pesajes.length - 1]?.fecha?.substring(0, 10);
                    const fechaUltima = pesajes[0]?.fecha?.substring(0, 10);
                    let diasTotales = null;
                    let gmdGlobal = null;
                    if (fechaPrimera && fechaUltima) {
                      try {
                        diasTotales = Math.round((new Date(fechaUltima) - new Date(fechaPrimera)) / (1000 * 60 * 60 * 24));
                        if (diasTotales > 0) gmdGlobal = gananciaTotal / diasTotales;
                      } catch { /* silent */ }
                    }
                    const gmdUltimoPeriodo = pesajes[0]?.gmd;
                    return (
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
                        <div className="text-center p-2 bg-white rounded-lg">
                          <p className="text-xs text-gray-500">Primer Peso</p>
                          <p className="text-lg font-bold text-gray-800">{Number(primerPeso).toFixed(2)} kg</p>
                          {fechaPrimera && <p className="text-xs text-gray-400">{formatDate(fechaPrimera)}</p>}
                        </div>
                        <div className="text-center p-2 bg-white rounded-lg">
                          <p className="text-xs text-gray-500">Ultimo Peso</p>
                          <p className="text-lg font-bold text-green-700">{Number(ultimoPeso).toFixed(2)} kg</p>
                          {fechaUltima && <p className="text-xs text-gray-400">{formatDate(fechaUltima)}</p>}
                        </div>
                        <div className="text-center p-2 bg-white rounded-lg">
                          <p className="text-xs text-gray-500">Ganancia Total</p>
                          <p className={`text-lg font-bold ${gananciaTotal > 0 ? 'text-blue-700' : gananciaTotal < 0 ? 'text-red-600' : 'text-gray-500'}`}>{gananciaTotal >= 0 ? '+' : ''}{gananciaTotal.toFixed(2)} kg</p>
                        </div>
                        <div className="text-center p-2 bg-white rounded-lg">
                          <p className="text-xs text-gray-500">Dias entre pesajes</p>
                          <p className="text-lg font-bold text-amber-700">{diasTotales != null ? `${diasTotales} d` : 'N/A'}</p>
                        </div>
                        <div className="text-center p-2 bg-white rounded-lg">
                          <p className="text-xs text-gray-500">GMD Global</p>
                          <p className="text-lg font-bold text-purple-700">{gmdGlobal != null ? `${Number(gmdGlobal).toFixed(3)} kg/d` : 'N/A'}</p>
                          {gmdUltimoPeriodo != null && gmdGlobal != null && Math.abs(gmdUltimoPeriodo - gmdGlobal) > 0.01 && (
                            <p className="text-xs text-gray-400">Ult. periodo: {Number(gmdUltimoPeriodo).toFixed(3)}</p>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {pesajes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-4xl mb-2">⚖️</p>
                  <p>No hay pesajes registrados</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pesajes.map((pesaje, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-sm font-semibold text-gray-700">
                              {formatDate(pesaje.fecha)}
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              {pesaje.edad_meses} meses
                            </span>
                            {pesaje.origen === 'rfid' && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                📡 RFID
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Peso:</span>
                              <span className="ml-2 font-semibold text-gray-900">{Number(pesaje.peso).toFixed(2)} kg</span>
                            </div>
                            {pesaje.altura_cadera && (
                              <div>
                                <span className="text-gray-500">Altura:</span>
                                <span className="ml-2 font-semibold text-gray-900">{pesaje.altura_cadera} cm</span>
                              </div>
                            )}
                            {pesaje.tasa_madurez && (
                              <div>
                                <span className="text-gray-500">Madurez:</span>
                                <span className="ml-2 font-semibold text-gray-900">{pesaje.tasa_madurez}%</span>
                              </div>
                            )}
                            {pesaje.frame && (
                              <div>
                                <span className="text-gray-500">Frame:</span>
                                <span className="ml-2 font-semibold text-gray-900">{pesaje.frame}</span>
                                {animal.raza && (
                                  <FrameBadge frame={pesaje.frame} raza={animal.raza} sexo={animal.sexo} />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: REPRODUCTIVO */}
          {activeTab === 'reproductivo' && (
            <div className="space-y-6">
              {/* Servicios */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Servicios ({servicios.length})
                </h3>
                {servicios.length === 0 ? (
                  <p className="text-gray-500 text-sm">No hay servicios registrados</p>
                ) : (
                  <div className="space-y-3">
                    {servicios.map((servicio, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-gray-900">{formatDate(servicio.fecha)}</p>
                            <p className="text-sm text-gray-600">{servicio.tipo_servicio}</p>
                            <p className="text-sm text-gray-600">Temporada: {servicio.temporada}</p>
                          </div>
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            servicio.resultado === 'preñada' ? 'bg-green-100 text-green-800' :
                            servicio.resultado === 'vacia' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {servicio.resultado}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pariciones */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Pariciones ({pariciones.length})
                </h3>
                {pariciones.length === 0 ? (
                  <p className="text-gray-500 text-sm">No hay pariciones registradas</p>
                ) : (
                  <div className="space-y-3">
                    {pariciones.map((paricion, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-purple-50">
                        <p className="font-semibold text-gray-900 mb-2">
                          {formatDate(paricion.fecha_paricion)}
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-600">Sexo ternero:</span>
                            <span className="ml-2 font-medium">{paricion.sexo_ternero}</span>
                          </div>
                          {paricion.peso_nacimiento && (
                            <div>
                              <span className="text-gray-600">Peso nac:</span>
                              <span className="ml-2 font-medium">{paricion.peso_nacimiento} kg</span>
                            </div>
                          )}
                          {paricion.peso_destete && (
                            <div>
                              <span className="text-gray-600">Peso destete:</span>
                              <span className="ml-2 font-medium">{paricion.peso_destete} kg</span>
                            </div>
                          )}
                          {paricion.eficiencia_productiva && (
                            <div className="col-span-2">
                              <span className="text-gray-600">EP:</span>
                              <span className="ml-2 font-bold text-green-600">
                                {paricion.eficiencia_productiva}%
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB: CONTROLES */}
          {activeTab === 'controles' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Controles de Macho ({controles_machos.length})
              </h3>
              {controles_machos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-4xl mb-2">🐂</p>
                  <p>No hay controles registrados</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {controles_machos.map((control, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-orange-50">
                      <p className="font-semibold text-gray-900 mb-3">
                        {formatDate(control.fecha)} - {control.edad_meses} meses
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        {control.ce_absoluta && (
                          <div>
                            <span className="text-gray-600">CE Absoluta:</span>
                            <span className="ml-2 font-medium">{control.ce_absoluta} mm</span>
                          </div>
                        )}
                        {control.ce_relativa && (
                          <div>
                            <span className="text-gray-600">CE Relativa:</span>
                            <span className="ml-2 font-medium">{control.ce_relativa} mm/kg</span>
                          </div>
                        )}
                        {control.calidad_seminal && (
                          <div>
                            <span className="text-gray-600">Calidad Seminal:</span>
                            <span className="ml-2 font-medium capitalize">{control.calidad_seminal}</span>
                          </div>
                        )}
                        {control.libido && (
                          <div>
                            <span className="text-gray-600">Libido:</span>
                            <span className="ml-2 font-medium">{control.libido}/10</span>
                          </div>
                        )}
                        {control.apto_reproductor !== null && (
                          <div className="col-span-2">
                            <span className="text-gray-600">Aptitud:</span>
                            <span className={`ml-2 font-semibold ${control.apto_reproductor ? 'text-green-600' : 'text-red-600'}`}>
                              {control.apto_reproductor ? '✅ Apto' : '❌ No Apto'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: SANITARIO */}
          {activeTab === 'sanitario' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Historial Sanitario ({eventos_sanitarios.length} eventos)
              </h3>
              {eventos_sanitarios.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Sin eventos sanitarios registrados</p>
              ) : (
                <div className="space-y-3">
                  {eventos_sanitarios.map((ev, i) => {
                    const colors = { vacunacion: 'border-green-400 bg-green-50', enfermedad: 'border-orange-400 bg-orange-50', medicamento: 'border-blue-400 bg-blue-50', mortandad: 'border-red-400 bg-red-50', lesion: 'border-yellow-400 bg-yellow-50', observacion: 'border-purple-400 bg-purple-50' };
                    return (
                      <div key={ev.id || i} className={`border-l-4 rounded-lg p-4 ${colors[ev.tipo] || 'border-gray-400 bg-gray-50'}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="text-sm font-bold text-gray-900 capitalize">{ev.tipo}</span>
                            <span className="text-xs text-gray-500 ml-3">{ev.fecha}</span>
                            {ev.gravedad && <span className={`ml-2 text-xs px-2 py-0.5 rounded font-medium ${ev.gravedad === 'grave' || ev.gravedad === 'critica' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{ev.gravedad}</span>}
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-gray-700 space-y-1">
                          {ev.vacuna && <p><strong>Vacuna:</strong> {ev.vacuna}</p>}
                          {ev.enfermedad && <p><strong>Enfermedad:</strong> {ev.enfermedad}</p>}
                          {ev.causa && <p><strong>Causa:</strong> {ev.causa}</p>}
                          {ev.tratamiento && <p><strong>Tratamiento:</strong> {ev.tratamiento} {ev.dosis ? `(${ev.dosis})` : ''}</p>}
                          {ev.ubicacion_lesion && <p><strong>Ubicacion:</strong> {ev.ubicacion_lesion}</p>}
                          {ev.detalle && <p><strong>Detalle:</strong> {ev.detalle}</p>}
                          {ev.observaciones && <p className="text-gray-500 italic">{ev.observaciones}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB: TIMELINE */}
          {activeTab === 'timeline' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Línea de Tiempo ({todosEventos.length} eventos)
              </h3>
              {todosEventos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No hay eventos registrados</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Línea vertical */}
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300"></div>

                  {/* Eventos */}
                  <div className="space-y-6">
                    {todosEventos.map((evento, idx) => (
                      <div key={idx} className="relative flex items-start space-x-4 pl-4">
                        {/* Icono */}
                        <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-white border-2 border-green-500">
                          <span className="text-sm">
                            {evento.tipo === 'pesaje' && '⚖️'}
                            {evento.tipo === 'servicio' && '💑'}
                            {evento.tipo === 'paricion' && '🍼'}
                            {evento.tipo === 'control' && '🐂'}
                          </span>
                        </div>

                        {/* Contenido */}
                        <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold text-gray-900">
                                {evento.tipo === 'pesaje' && 'Pesaje'}
                                {evento.tipo === 'servicio' && 'Servicio'}
                                {evento.tipo === 'paricion' && 'Parición'}
                                {evento.tipo === 'control' && 'Control Macho'}
                              </p>
                              <p className="text-sm text-gray-500">{formatDate(evento.fecha)}</p>
                            </div>
                          </div>

                          {/* Detalles según tipo */}
                          {evento.tipo === 'pesaje' && (
                            <p className="text-sm text-gray-600">
                              Peso: <strong>{Number(evento.data.peso).toFixed(2)} kg</strong>
                              {evento.data.tasa_madurez && ` • Madurez: ${evento.data.tasa_madurez}%`}
                            </p>
                          )}
                          {evento.tipo === 'servicio' && (
                            <p className="text-sm text-gray-600">
                              Tipo: <strong>{evento.data.tipo_servicio}</strong>
                              {' • '}Resultado: <strong>{evento.data.resultado}</strong>
                            </p>
                          )}
                          {evento.tipo === 'paricion' && (
                            <p className="text-sm text-gray-600">
                              Ternero: <strong>{evento.data.sexo_ternero}</strong>
                              {evento.data.eficiencia_productiva && ` • EP: ${evento.data.eficiencia_productiva}%`}
                            </p>
                          )}
                          {evento.tipo === 'control' && (
                            <p className="text-sm text-gray-600">
                              {evento.data.ce_absoluta && `CE: ${evento.data.ce_absoluta} mm`}
                              {evento.data.apto_reproductor !== null && ` • ${evento.data.apto_reproductor ? 'Apto' : 'No Apto'}`}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnimalDetailPage;
